import {
  Inter_200ExtraLight,
  Inter_300Light,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  useFonts,
} from "@expo-google-fonts/inter";
import { Lora_500Medium } from "@expo-google-fonts/lora";
import "@expo/metro-runtime";
import { t } from "@lingui/macro";
import { I18nProvider } from "@lingui/react";
import { OverlayProvider } from "@react-native-aria/overlays";
import * as Sentry from "@sentry/react-native";
import { ThemeProvider as ShopifyProvider } from "@shopify/restyle";
import "expo-dev-client";
import * as SplashScreen from "expo-splash-screen";
import React, { useCallback, useEffect, useState } from "react";
import { Platform, StatusBar } from "react-native";
import "react-native-get-random-values";
import "react-native-reanimated";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { enableFreeze } from "react-native-screens";

import { useInitializeMixpanel } from "@/utils/Analytics/Mixpanel/useInitializeMixpanel/useInitializeMixpanel";
import { ModalTransitionProvider } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { WhooshToastProvider } from "@whoosh/design-system/src/components/overlay/Toast/WhooshToastProvider";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { theme as ShopifyTheme } from "@whoosh/design-system/src/theme/restyle/theme";

import TimesSemibold from "./../../packages/@whoosh/design-system/src/assets/fonts/Times-Semibold.otf";
import TimesRegular from "./../../packages/@whoosh/design-system/src/assets/fonts/Times.ttf";
import { ReviewBackendIndicator } from "./src/components/ReviewBackendIndicator";
import { SentryError, SignOutButton } from "./src/features/ErrorHandling";
import { PlatformSpecificErrorBoundary } from "./src/features/ErrorHandling/components/PlatformSpecificErrorBoundary";
import { LanguagePreferenceProvider } from "./src/features/LanguagePreference";
import { OtaUpdateOverlayProvider } from "./src/features/OtaUpdateOverlay";
import { OverlayLoader } from "./src/features/OverlayLoader";
import { NavigatorRenderer } from "./src/navigators/SessionChecker/NavigatorRenderer";
import { i18n } from "./src/utils/Translations/i18nBootstrap";
import { ExpoUpdatesManager } from "./src/utils/expoUpdates/ExpoUpdatesManager";
import { useGlobalStyles } from "./src/utils/globalStyles/useGlobalStyles";
import { LaunchDarklyProvider } from "./src/utils/launchDarkly/launchDarklyProvider";
import { NativeAppVersionCheckProvider } from "./src/utils/nativeAppVersion/NativeAppVersionCheckProvider";
import {
  copySentryReplayUrlToClipboard,
  isEditableEventTarget,
  isSentryReplayCopyShortcut,
} from "./src/utils/sentry/initializeSentry";

// This is needed for react-native-screens
enableFreeze(true);

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

const WEB_FONT_LOAD_TIMEOUT_MS = 3000;

function App() {
  const [appIsReady, setAppIsReady] = useState(false);
  const [fontLoadTimedOut, setFontLoadTimedOut] = useState(false);
  const [localeReady, setLocaleReady] = useState(false);

  let [fontsLoaded] = useFonts({
    Inter_200ExtraLight,
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Lora_500Medium,
    "Times-Regular": TimesRegular,
    "Times-Semibold": TimesSemibold,
  });

  useEffect(() => {
    if (Platform.OS !== "web" || fontsLoaded || fontLoadTimedOut) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setFontLoadTimedOut(true);
    }, WEB_FONT_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [fontLoadTimedOut, fontsLoaded]);

  const fontsReady = fontsLoaded || fontLoadTimedOut;
  const handleLocaleReady = useCallback(() => setLocaleReady(true), []);

  useEffect(() => {
    const hideSplashScreen = async () => {
      await SplashScreen.hideAsync();
    };
    if (fontsReady && localeReady && !appIsReady) {
      hideSplashScreen().then(() => {
        setAppIsReady(true);
      });
    }
  }, [appIsReady, fontsReady, localeReady]);

  useGlobalStyles();
  useInitializeMixpanel();

  if (!fontsReady) {
    return null;
  } else {
    return (
      <ShopifyProvider theme={ShopifyTheme}>
        <LanguagePreferenceProvider onReady={handleLocaleReady}>
          {() => (
            <I18nProvider i18n={i18n}>
              <SafeAreaProvider>
                <WhooshToastProvider>
                  <ModalTransitionProvider>
                    <SentryReplayShortcutListener />
                    <LaunchDarklyProvider>
                      <NativeAppVersionCheckProvider>
                        <OverlayProvider>
                          <OtaUpdateOverlayProvider>
                            <ExpoUpdatesManager />
                            <StatusBar
                              animated={true}
                              barStyle="dark-content"
                              backgroundColor="transparent"
                              translucent
                            />
                            <ReviewBackendIndicator />
                            <PlatformSpecificErrorBoundary
                              fallback={(eventId, error, resetError) => {
                                return (
                                  <SentryError
                                    resetError={resetError}
                                    signOutButton={
                                      <SignOutButton {...{ resetError }} />
                                    }
                                    eventId={eventId}
                                  />
                                );
                              }}
                            >
                              <NavigatorRenderer />
                            </PlatformSpecificErrorBoundary>
                            <OverlayLoader />
                          </OtaUpdateOverlayProvider>
                        </OverlayProvider>
                      </NativeAppVersionCheckProvider>
                    </LaunchDarklyProvider>
                  </ModalTransitionProvider>
                </WhooshToastProvider>
              </SafeAreaProvider>
            </I18nProvider>
          )}
        </LanguagePreferenceProvider>
      </ShopifyProvider>
    );
  }
}

const SentryReplayShortcutListener = () => {
  const { showToast } = useToast();

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    const handleReplayShortcut = async (event: KeyboardEvent) => {
      if (!isSentryReplayCopyShortcut(event)) {
        return;
      }

      if (isEditableEventTarget(event.target)) {
        return;
      }

      event.preventDefault();

      try {
        const replayUrl = await copySentryReplayUrlToClipboard();
        showToast({
          semanticStyle: "success",
          title: t`Sentry Replay URL copied`,
          description: replayUrl,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";

        if (message === "No Active Sentry Replay") {
          showToast({
            semanticStyle: "error",
            title: t`No Active Sentry Replay`,
          });
          return;
        }

        showToast({
          semanticStyle: "error",
          title: t`Error copying Sentry Replay URL`,
          description: message,
        });
      }
    };

    window.addEventListener("keydown", handleReplayShortcut);
    return () => {
      window.removeEventListener("keydown", handleReplayShortcut);
    };
  }, [showToast]);

  return null;
};

export default Sentry.wrap(App);
